import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { Component } from 'react';
import { connect } from 'react-redux';
import { Functional } from '@wayfinder/vms-base';
import { LandscapeState } from '@wayfinder/vms-state';
import LliamUtilities from '@locuslabs/lliam-utilities';
import * as constants from '../../backend/constants';
import * as VmsState from '../../backend/index-vms-state';
import * as Gtag from '../../backend/gtag';
import { BulkChangeConfigurator, PoiList, TextFieldEditor, Button } from '../../components';
import downloadPoiSvg from '../../ext/svg/image.export.white.svg';

import s from './PoiListContainer.module.scss';

const { Routing } = VmsState;
const MAX_POI_TO_SELECT_ALL = 50;

class PoiListContainer extends Component {
    static propTypes = {
        accountId: PropTypes.string,
        bulkEditIds: PropTypes.object,
        bulkEditModalOpen: PropTypes.bool,
        bulkEditSaving: PropTypes.bool,
        categories: PropTypes.arrayOf(PropTypes.object),
        className: PropTypes.string,
        currentPoiId: PropTypes.string,
        dispatch: PropTypes.func,
        getBadgeOptions: PropTypes.func,
        hasLoaded: PropTypes.bool,
        isV5: PropTypes.bool,
        isAllSelected: PropTypes.bool,
        hasMapSyndicationPermission: PropTypes.bool,
        loading: PropTypes.bool,
        location: PropTypes.object,
        poiList: PropTypes.array,
        poiSearchText: PropTypes.string,
        poiStatus: PropTypes.object,
        venueId: PropTypes.string,
    };

    static defaultProps = {
        accountId: '',
        bulkEditIds: {},
        bulkEditModalOpen: false,
        bulkEditSaving: false,
        categories: [],
        className: '',
        currentPoiId: '',
        dispatch: () => {},
        getBadgeOptions: () => {},
        hasLoaded: false,
        isV5: true,
        isAllSelected: false,
        hasMapSyndicationPermission: false,
        loading: true,
        location: {},
        poiList: [],
        poiSearchText: '',
        poiStatus: {},
        venueId: '',
    };

    addPoi = () => {
        const { dispatch, venueId } = this.props;
        dispatch(VmsState.PoiStatusReducers.Actions.startNewPoi(venueId));
        dispatch(Routing.Actions.goToPoi(venueId, `${venueId}_NEW`));
    };

    toggleSelectAllId = () => {
        const { poiList, dispatch, isAllSelected, poiStatus } = this.props;
        
        const openUuids = R.filter(poi => {
            // Filter out closed id's
            return !R.startsWith('#', poi.id)
        }, poiList); // poiList is an array of each visible (filtered) poi & metadata
        const uuids = R.map(poi => poi.uuid, openUuids);
        const uuidsObj = R.zipObj(
            uuids,
            R.map(() => !isAllSelected, uuids),
        );
        if (R.length(poiList) <= MAX_POI_TO_SELECT_ALL) {
            // Warning: temp MAX list put in place until officially decided
            dispatch(VmsState.BulkEditsReducer.Actions.selectAll());
            dispatch(VmsState.BulkEditsReducer.Actions.bulkEditPoiList(uuidsObj));
        }
    };

    toggleBulkEditId = uuid => {
        const { bulkEditIds, dispatch } = this.props;
        const action = bulkEditIds[uuid] ? VmsState.BulkEditsReducer.Actions.dontBulkEditPoi : VmsState.BulkEditsReducer.Actions.bulkEditPoi;
        dispatch(action(uuid));
    };

    startBulkEdit = () => {
        const { dispatch } = this.props;
        dispatch(VmsState.BulkEditsReducer.Actions.configuringBulkEdits());
    };

    downloadPois = () => {
        const { dispatch, venueId, hasMapSyndicationPermission } = this.props;
        dispatch(VmsState.MultiThunks.downloadPois(venueId, hasMapSyndicationPermission));
    };

    getQuery = () => (dispatch, getState) => getState().location.query;

    mergeTagArrays = (entityTagArray, arrayEdit, arrayEditType) => {
        switch (arrayEditType) {
            case constants.BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.ARRAY_ADD:
                return R.concat(entityTagArray, arrayEdit);
            case constants.BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.ARRAY_REMOVE:
                return R.without(arrayEdit, entityTagArray);
            case constants.BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.REPLACE:
                return arrayEdit;
            default:
                return [];
        }
    };

    mergeEntity = (entity, edits, arrayEditTypes) => {
        const { displayTagsEditType, hiddenTagsEditType } = arrayEditTypes;
        const { displayTags, tags } = edits; // edit types that are specifically arrays

        // replace edits displayTags parameter with merged entity and edits array
        if (displayTagsEditType && edits.displayTags) {
            edits.displayTags = this.mergeTagArrays(entity.displayTags, displayTags, displayTagsEditType);
        }

        // replace edits tags parameter with merged entity and edits array
        if (hiddenTagsEditType && edits.tags) {
            edits.tags = this.mergeTagArrays(entity.tags, tags, hiddenTagsEditType);
        }

        return R.mergeRight(entity, edits);
    };

    onCompleteBulkEdit = (result, arrayEditTypes) => {
        const { dispatch, bulkEditIds, venueId, poiList } = this.props;
        if (result) {
            const idArray = [];
            R.forEachObjIndexed((isEdited, id) => {
                if (isEdited) {
                    idArray.push(id);
                }
            }, bulkEditIds);

            const edits = R.map(e => e.value, result);
            const pois = R.filter(e => idArray.includes(e.uuid), poiList);
            const entities = R.map(e => ({ entity: this.mergeEntity(e, R.clone(edits), arrayEditTypes) }), pois);

            R.map(entity => dispatch(LandscapeState.actionCreators.updateEntity(entity)), entities);
            dispatch(VmsState.MultiThunks.updateEntities(venueId, idArray, result, true, null, entities));
        } else {
            dispatch(VmsState.BulkEditsReducer.Actions.cancelBulkEdits());
        }
    };

    onPoiSelect = poiId => {
        const { dispatch, venueId, currentPoiId } = this.props;
        const query = dispatch(this.getQuery());
        const tabQuery = query && R.has('tab', query) ? { tab: query.tab } : {};

        // turn OFF the row and return the default venue state
        if (poiId === currentPoiId) dispatch(Routing.Actions.goToVenue(venueId));
        // clicked on row
        else if (currentPoiId) dispatch(Routing.Actions.goToPoi(venueId, poiId, tabQuery));
        // debounce the dispatch since a user could hold down the up/down arrow keys
        else {
            const f = Functional.debounce(dispatch, 100);
            f(Routing.Actions.goToPoi(venueId, poiId, tabQuery));
        }
    };

    onSearchTextChanged = searchText => {
        const { dispatch, venueId } = this.props;
        dispatch(VmsState.ViewReducer.Actions.changePoiSearchText(venueId, searchText));
    };

    /*
     * Get the position of a child element on the window relative to the parent element
     * getRelativePosition :: (Element, Element) -> Object
     */
    getRelativePosition = (parent, child) => {
        const parentRect = parent.getBoundingClientRect();
        const childRect = child.getBoundingClientRect();
        return {
            top: childRect.top - parentRect.top,
            left: childRect.left - parentRect.left,
            right: childRect.right - parentRect.right,
            bottom: childRect.bottom - parentRect.bottom,
        };
    };

    /*
     * From the parent table element
     * If the child element being selected is not in the viewport scroll so that it's visible
     * scrollToRow :: (Entity, Boolean) -> ()
     */
    scrollToRow = (entity, top) => {
        const table = document.querySelector('.rt-tbody');

        const findIdNode = node => {
            const tdChildren = R.path(['childNodes', 0, 'childNodes'], node);
            const result = R.find(column => column.innerText === entity.id, tdChildren);
            return result;
        };
        const element = R.find(findIdNode, table.children);

        if (!element) return;

        // If the top of the bounding box is negative the top of the box is above the parent element.
        // If the bottom of the bounding box is positive the box is below the parent element.
        const rect = this.getRelativePosition(table, element);
        const isNotVisible = rect.top <= 0 || rect.bottom >= 0;

        if (isNotVisible) element.scrollIntoView(top);
    };

    onKeyDown = event => {
        const { key } = event;
        const { currentPoiId, poiList } = this.props;

        // Go to the previous entity in the list
        if (key === 'ArrowUp') {
            const currentIndex = R.findIndex(entity => entity.uuid === currentPoiId, poiList);
            const nextEntity = poiList[R.clamp(0, poiList.length - 1, currentIndex - 1)];
            this.onPoiSelect(nextEntity.uuid);
            this.scrollToRow(nextEntity, true);
        }

        // Go to the next entity in the list
        if (key === 'ArrowDown') {
            let currentIndex = R.findIndex(entity => entity.uuid === currentPoiId, poiList);
            // R.findIndex will return -1 if it can't find the id. When we're going down, we want to set it to the last
            // entity in the list. Arrow up will clamp to 0 so no need for a guard there, we want it to be 0 there.
            if (currentIndex === -1) currentIndex = poiList.length - 1;

            const nextEntity = poiList[R.clamp(0, poiList.length - 1, currentIndex + 1)];
            this.onPoiSelect(nextEntity.uuid);
            this.scrollToRow(nextEntity, false);
        }
    };

    onDownloadPoiHover = () => {
        const { venueId, hasMapSyndicationPermission, bulkEditIds } = this.props;

        const bulkEditCount = R.filter(R.identity, R.values(bulkEditIds)).length;

        // only record event for users that do not have map syndication permission and not bulk editing
        // had to attach the mouse event to the div that surrounds the button because browser events do not fire
        // for elements that are disabled
        if (!hasMapSyndicationPermission && bulkEditCount === 0) Gtag.recordHoverDownloadPoiButton(venueId);
    };

    render() {
        const {
            poiList,
            loading,
            poiSearchText,
            currentPoiId,
            className,
            categories,
            getBadgeOptions,
            isAllSelected,
            poiStatus,
            bulkEditIds,
            bulkEditModalOpen,
            bulkEditSaving,
            isV5,
            hasLoaded,
            accountId,
            venueId,
            hasMapSyndicationPermission,
            isTranslatedVenue
        } = this.props;

        const bulkEditCount = R.filter(R.identity, R.values(bulkEditIds)).length;

        return (
            <div className={classNames(s.poiListContainer, className)}>
                <div className={s.innerWrapper}>
                    <div className={s.infoWrapper}>
                        <div className={s.titleWrapper}>
                            <div className={s.title}>Points of Interest</div>
                            <div className={s.poiCount}>({poiList.length})</div>
                        </div>
                        <div className={s.subtitle}>Below you can view all POIs associated with this venue</div>
                        <div className={s.searchAddWrapper}>
                            <TextFieldEditor className={s.searchBox} iconLeft="icon-search" onChange={this.onSearchTextChanged} placeHolder="Search for a POI..." value={poiSearchText} />
                            <div className={s.buttonWrapper} onMouseEnter={this.onDownloadPoiHover}>
                                {!!bulkEditCount && (
                                    <button type="button" className={s.bulkEditButton} onClick={this.startBulkEdit}>
                                        BULK EDIT ({bulkEditCount})
                                    </button>
                                )}
                                {!isV5 && (
                                    <button type="button" className={s.addPoiButton} onClick={this.addPoi}>
                                        ADD POI
                                    </button>
                                )}
                                <Button
                                    type="button"
                                    className={classNames(s.downloadPoisButton)}
                                    label="Download POIs"
                                    color="blue"
                                    onClick={this.downloadPois}
                                    iconClass={s.downloadIcon}
                                    disabled={!hasLoaded}
                                >
                                    <img src={downloadPoiSvg} alt="Download POIs" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div onKeyDown={this.onKeyDown} className={s.listWrapper} tabIndex="0">
                        <PoiList
                            bulkEditIds={bulkEditIds}
                            className={s.list}
                            currentPoiId={currentPoiId}
                            hasLoaded={hasLoaded}
                            loading={loading}
                            onPoiSelect={this.onPoiSelect}
                            poiStatus={poiStatus}
                            poisToRender={poiList}
                            toggleBulkEditId={this.toggleBulkEditId}
                            isAllSelected={isAllSelected}
                            toggleSelectAllId={this.toggleSelectAllId}
                            poiSearchText={poiSearchText}
                            maxLengthSelectAll={MAX_POI_TO_SELECT_ALL}
                        />
                    </div>
                </div>
                {bulkEditModalOpen && (
                    <BulkChangeConfigurator
                        accountId={accountId}
                        categories={categories}
                        getBadgeOptions={getBadgeOptions}
                        isV5={isV5}
                        onClose={this.onCompleteBulkEdit}
                        open
                        saving={bulkEditSaving}
                        selectedPoiCount={bulkEditCount}
                        venueId={venueId}
                        isTranslatedVenue={isTranslatedVenue}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const venueId = Routing.Selectors.getLocationVenueId(state);
    const userData = VmsState.SessionReducer.Selectors.getUserData(state);
    const isInternal = R.prop('custom:Internal', userData);
    const hasMapSyndicationPermission = VmsState.SessionReducer.Selectors.hasReadPermissionToResource(venueId, LliamUtilities.CONSTANTS.MAP_SYNDICATION, state);
    const categories = LandscapeState.selectors.categoriesForVenue(venueId, isInternal, state);
    const isV5 = LandscapeState.selectors.isVenueV5(venueId, state);
    const isAllSelected = VmsState.BulkEditsReducer.Selectors.isAllSelected(state);
    const venueDetails = LandscapeState.selectors.getVenueSummary(venueId);
    const isTranslatedVenue = venueDetails?.locale && venueDetails.locale !== 'en';

    return {
        venueId,
        categories,
        isV5,
        isAllSelected,
        hasMapSyndicationPermission,
        accountId: VmsState.SessionReducer.Selectors.getAccountId(state),
        poiSearchText: VmsState.ViewReducer.Selectors.getPoiSearchText(state),
        currentPoiId: Routing.Selectors.getLocationPoiId(state),
        location: state.location,
        poiStatus: VmsState.PoiStatusReducers.Selectors.getPoiStatus(state),
        bulkEditIds: VmsState.BulkEditsReducer.Selectors.getBulkEditIds(state),
        bulkEditModalOpen: VmsState.BulkEditsReducer.Selectors.isConfiguringBulkEdits(state),
        bulkEditSaving: VmsState.BulkEditsReducer.Selectors.isSavingBulkEdits(state),
        hasLoaded: LandscapeState.selectors.hasVenueLoaded(state),
        getBadgeOptions: LandscapeState.selectors.getBadgeOptions,
        isTranslatedVenue,
    };
};

export default connect(mapStateToProps)(PoiListContainer);
